import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { DBO_ARR, DBO_V } from '@op/utils/format';
import { getChartBufferWidth } from '@op/utils/systemUtil';
import * as echarts from 'echarts';
import React, { useMemo } from 'react';
import { LiveTimelineType } from '../../type';

export default function TableMiniChart({
  data,
  name = 'CCV',
  colorMap = {
    areaStart: 'rgba(55, 106, 231, 0.3)',
    areaEnd: 'rgba(209,230,250,0.00)',
    line: '#376AE7',
  },
  height = 30,
}: {
  data?: LiveTimelineType;
  name?: string;
  colorMap?: {
    areaStart: string;
    areaEnd: string;
    line: string;
  };
  height?: number;
}) {
  const { series, xAxis, isEmpty } = useMemo(() => {
    const series: Array<number> = [];
    const { timeline = [] } = data || {};
    if ((timeline || []).length === 0) {
      return {
        series,
        xAxis: [],
        isEmpty: true,
      };
    }

    timeline.sort(DBO_ARR.sortByTime('time', 'asc'));

    const xAxis = timeline
      .filter((item) => (item?.live_viewer || 0) !== 0)
      .map((item) => {
        series.push(item.live_viewer || 0);
        return item.time;
      });

    return {
      series,
      xAxis,
      isEmpty: series.length === 0,
    };
  }, [data]);

  const options = useMemo(() => {
    if (isEmpty) return {};
    const seriesData: any[] = [
      {
        symbol: 'circle',
        data: series,
        type: 'line',
        symbolSize: 0,
        lineStyle: {
          width: 2,
          color: colorMap.line,
        },
        itemStyle: {
          color: colorMap.line,
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: colorMap.areaStart,
            },
            {
              offset: 1,
              color: colorMap.areaEnd,
            },
          ]),
        },
      },
    ];

    const option = {
      tooltip: {
        appendToBody: true,
        position() {
          return { top: -80, right: -10 };
        },
        confine: false,
        formatter: (param: any) => {
          const { axisValue } = param?.[0] || {};
          return formatChartParamsDate({
            params: param,
            listTitleFunc: () => name,
            valueFormat: (value) => DBO_V.reviews(value),
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: name,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: listTimeTitleFunc(axisValue, 'hourly'),
                  },
                ],
              },
            ],
          });
        },
      },
      legend: {
        show: false,
      },
      grid: {
        top: '5px',
        left: getChartBufferWidth(5),
        right: '5px',
        bottom: '5px',
        containLabel: false,
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: 0,
          data: xAxis,
          axisLabel: false,
          axisLine: false,
          axisTick: false,
          splitLine: false,
        },
      ],
      yAxis: {
        type: 'value',
        splitLine: false,
        axisLabel: false,
        boundaryGap: 0,
      },
      series: seriesData,
    };

    return option;
  }, [xAxis, name, isEmpty]);


  if(isEmpty) return null;

  return (
    <EChartCommon
      options={options}
      height={height}
      empty={isEmpty}
      hasEmptyImg={false}
      hasEmptyTitle={false}
      optionsProps={{ excludeOptions: ['axisPointer'] }}
    ></EChartCommon>
  );
}
